// JEDNO zrodlo prawdy dla ksztaltu i walidacji adresu sklepu (kafelek
// „ZAMOW SUSHI DO DOMU"). Plik jest CELOWO wolny od zaleznosci — importuja go
// oba bundle:
//   - przegladarka/SSR: src/app/... (panel admina, serwis publiczny),
//   - Express API:      src/api/routes/shop-config.api.ts.
// Druga implementacja walidacji po stronie serwera oznaczalaby, ze panel
// przepuszcza adres, ktory API odrzuca (albo odwrotnie) — i nikt by tego nie
// zauwazyl do pierwszego zgloszenia od uzytkownika.

/**
 * Adres awaryjny — dokladnie ten, ktory do tej pory byl zaszyty w kodzie
 * (home.component.ts / client-menu.component.ts). Uzywany, gdy API nie
 * odpowiada: kafelek ma DZIALAC nawet przy padnietej bazie.
 */
export const SHOP_URL_FALLBACK = 'https://drzewowisni.goorder.pl';

export type ShopUrlError = 'required' | 'not_a_string' | 'invalid_url' | 'invalid_scheme';

export interface ShopConfig {
  /** Adres dla stron polskich. Zawsze ustawiony. */
  pl: string;
  /** Adres dla stron angielskich; null == „uzyj polskiego". */
  en: string | null;
}

/**
 * Sprowadza wejscie z formularza/API do postaci zapisywalnej w bazie.
 *
 * Kontrakt:
 *  - null / undefined / sam bialy znak  -> `{ ok: true, value: null }` (puste pole EN),
 *  - cokolwiek co nie jest stringiem    -> `not_a_string`,
 *  - adres nieparsowalny albo wzgledny  -> `invalid_url`,
 *  - schemat inny niz http/https        -> `invalid_scheme`.
 *
 * `javascript:alert(1)` i `data:text/html,...` parsuja sie poprawnie przez
 * `new URL()`, wiec SAMO parsowanie niczego nie broni — odrzuca je dopiero
 * jawna whitelist schematow ponizej. To jest powod istnienia tej funkcji:
 * adres trafia prosto do atrybutu `href`, czyli do kontekstu wykonania.
 *
 * `//evil.example` (protocol-relative) NIE przechodzi: `new URL()` bez bazy
 * rzuca -> `invalid_url`.
 */
export function normalizeShopUrl(raw: unknown): { ok: true; value: string | null } | { ok: false; error: ShopUrlError } {
  if (raw === null || raw === undefined) {
    return { ok: true, value: null };
  }
  if (typeof raw !== 'string') {
    return { ok: false, error: 'not_a_string' };
  }
  const trimmed = raw.trim();
  if (trimmed === '') {
    return { ok: true, value: null };
  }

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return { ok: false, error: 'invalid_url' };
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return { ok: false, error: 'invalid_scheme' };
  }
  // Ostatni straznik: adres bez hosta nie jest adresem sklepu. UWAGA — dla
  // http/https WHATWG i tak wymaga hosta (`http:///x` daje host 'x', a nie
  // pusty), wiec w praktyce ta galaz nie odpala sie dla przepuszczonych
  // schematow. Zostaje jako pas bezpieczenstwa; fakt przypiety w spec-u.
  if (!parsed.host) {
    return { ok: false, error: 'invalid_url' };
  }
  return { ok: true, value: trimmed };
}

/** Wariant dla pola OBOWIAZKOWEGO (PL): puste jest bledem, nie `null`. */
export function normalizeRequiredShopUrl(raw: unknown): { ok: true; value: string } | { ok: false; error: ShopUrlError } {
  const result = normalizeShopUrl(raw);
  if (!result.ok) {
    return result;
  }
  if (result.value === null) {
    return { ok: false, error: 'required' };
  }
  return { ok: true, value: result.value };
}

/**
 * Adres kafelka dla podanego jezyka — JEDYNE miejsce, w ktorym zapada regula
 * „puste EN => polski". Zwraca fallback takze wtedy, gdy config w ogole nie
 * dotarl (null), zeby wolajacy nie musial powtarzac tego warunku.
 */
export function resolveShopUrl(config: ShopConfig | null | undefined, locale: string | null | undefined): string {
  if (!config) {
    return SHOP_URL_FALLBACK;
  }
  if (locale === 'en') {
    const en = typeof config.en === 'string' ? config.en.trim() : '';
    if (en !== '') {
      return en;
    }
  }
  const pl = typeof config.pl === 'string' ? config.pl.trim() : '';
  return pl !== '' ? pl : SHOP_URL_FALLBACK;
}
